import {
	RedirectToSignIn,
	useAuth,
	useClerk,
	useUser,
} from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { UpgradeSection } from "../components/UpgradeSection";
import { trackEvent } from "../lib/analytics";
import { FREE_DAILY_LIMIT_CONST, getUsageToday } from "../lib/freeTier/limits";
import { useLicense } from "../lib/stripe/useLicense";

const WORKERS_URL =
	import.meta.env.VITE_WORKERS_URL ??
	"https://stripe-gateway.fucina.workers.dev";

export default function MyPage() {
	const { isSignedIn, isLoaded, getToken } = useAuth();
	const { signOut } = useClerk();
	const { user } = useUser();
	const { license, isLoading, isPro, portalUrl } = useLicense();
	const [usageToday, setUsageToday] = useState(0);
	const [portalLoading, setPortalLoading] = useState(false);
	const [portalError, setPortalError] = useState<string | null>(null);
	const authEventFiredRef = useRef(false);

	useEffect(() => {
		setUsageToday(getUsageToday());
	}, []);

	useEffect(() => {
		if (authEventFiredRef.current) return;
		const params = new URLSearchParams(window.location.search);
		if (params.get("signup") === "1") {
			authEventFiredRef.current = true;
			trackEvent("sign_up", { method: "clerk" });
			window.history.replaceState({}, "", "/mypage");
		} else if (params.get("signin") === "1") {
			authEventFiredRef.current = true;
			trackEvent("login", { method: "clerk" });
			window.history.replaceState({}, "", "/mypage");
		}
	}, []);

	const handlePortal = async () => {
		setPortalLoading(true);
		setPortalError(null);
		try {
			const token = await getToken();
			const res = await fetch(`${WORKERS_URL}/portal`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({
					project: "shironukiya-saas",
					return_url: window.location.href,
				}),
			});
			if (!res.ok) throw new Error("ポータルURLの取得に失敗しました");
			const data = (await res.json()) as { url: string };
			window.location.href = data.url;
		} catch {
			setPortalError("ポータルへの接続に失敗しました。");
		} finally {
			setPortalLoading(false);
		}
	};

	const handleSignOut = () => {
		signOut({ redirectUrl: "/" });
	};

	// Clerk初期化待ち
	if (!isLoaded || isLoading) {
		return (
			<main className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
			</main>
		);
	}

	// 未認証時はClerkのリダイレクトコンポーネントを使用
	if (!isSignedIn) {
		return <RedirectToSignIn />;
	}

	return (
		<main className="min-h-screen bg-gray-50 py-12 px-4">
			<div className="max-w-lg mx-auto">
				<div className="flex items-center justify-between mb-6">
					<a href="/" className="text-gray-400 hover:text-gray-600 text-sm">
						← トップへ
					</a>
					<button
						type="button"
						onClick={handleSignOut}
						className="text-sm text-gray-500 hover:text-gray-700 transition"
					>
						ログアウト
					</button>
				</div>

				<h1 className="text-2xl font-bold text-gray-900 mb-2">マイページ</h1>
				{user?.primaryEmailAddress && (
					<p className="text-sm text-gray-500 mb-6">
						{user.primaryEmailAddress.emailAddress}
					</p>
				)}

				{isPro && license ? (
					// Proユーザー
					<div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
						<div className="flex items-center gap-3">
							<span className="text-2xl">✨</span>
							<div>
								<p className="font-bold text-gray-900">プロプラン</p>
								<p className="text-sm text-gray-500">
									{license.plan === "yearly" ? "年額 ¥5,000" : "月額 ¥500"}
								</p>
							</div>
							<span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
								利用中
							</span>
						</div>

						<div className="border-t border-gray-100 pt-4 space-y-3 text-sm">
							{license.subscription_start && (
								<div className="flex justify-between">
									<span className="text-gray-500">契約開始日</span>
									<span className="text-gray-800">
										{new Date(license.subscription_start).toLocaleDateString(
											"ja-JP",
										)}
									</span>
								</div>
							)}
							{(license.current_period_end ?? license.expires_at) && (
								<div className="flex justify-between">
									<span className="text-gray-500">次回更新日</span>
									<span className="text-gray-800">
										{new Date(
											(license.current_period_end ?? license.expires_at)!,
										).toLocaleDateString("ja-JP")}
									</span>
								</div>
							)}
							<div className="flex justify-between">
								<span className="text-gray-500">本日の利用</span>
								<span className="text-gray-800">無制限</span>
							</div>
						</div>

						{portalError && (
							<p className="text-xs text-red-600">{portalError}</p>
						)}

						<button
							type="button"
							onClick={handlePortal}
							disabled={portalLoading}
							className="w-full mt-2 border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 font-medium py-2.5 rounded-xl transition-colors text-sm"
						>
							{portalLoading ? "処理中..." : "解約・プラン変更"}
						</button>
					</div>
				) : (
					// Freeユーザー
					<div className="space-y-4">
						<div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
							<div className="flex items-center gap-3 mb-4">
								<span className="text-2xl">🎁</span>
								<div>
									<p className="font-bold text-gray-900">フリープラン</p>
									<p className="text-sm text-gray-500">
										毎日{FREE_DAILY_LIMIT_CONST}枚まで無料
									</p>
								</div>
							</div>
							<div className="border-t border-gray-100 pt-4 text-sm">
								<div className="flex justify-between">
									<span className="text-gray-500">本日の利用</span>
									<span className="text-gray-800 font-medium">
										{usageToday} / {FREE_DAILY_LIMIT_CONST}回
									</span>
								</div>
							</div>
						</div>
						<UpgradeSection
							license={license}
							isLoading={isLoading}
							portalUrl={portalUrl}
							context="mypage"
						/>
					</div>
				)}
			</div>
		</main>
	);
}
